import Route from '@ember/routing/route';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { setUser as setSentryUser } from '@sentry/ember';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { isInDevMode } from 'sh-www/utils/misc';
import { setUserId } from 'sh-www/utils/datalayer';
import { filterNullable } from 'sh-www/utils/object';
import { safeScriptLoad } from 'sh-www/utils/script-loader';

import config from 'sh-www/config/environment';
import EmberRoutes from 'sh-www/ember-routes';

const DONT_POPULATE_USER_ROUTES = new Set([
  EMBER_ROUTE_NAMES.MAINTENANCE,
  EMBER_ROUTE_NAMES.SIGNIN,
  EMBER_ROUTE_NAMES.VERIFY_EMAIL,
]);

class ApplicationRoute extends Route {
  /**
   * Register services
   */
  @service('error-tracker') errorTracker;
  @service api;
  @service router;
  @service session;
  @service store;

  /**
   * @inheritDoc
   */
  async beforeModel(transition) {
    await this.session.setup();
    if (config.DEBUG) {
      console.log(`APPLICATION - Session Setup Complete`);
    }

    if (!this.session.isAuthenticated || DONT_POPULATE_USER_ROUTES.has(transition.targetName)) {
      return;
    }

    const currentUser = await this.session.loadCurrentUser();

    if (!currentUser) {
      this.errorTracker.error(
        'application route tried to loadCurrentUser from the session but it failed',
        this,
      );

      await this.session.clearStore();
    }
  }

  redirect() {
    const { currentUser } = this.session;

    // TODO Move this to loadCurrentUser method so it isn't just on page load
    if (currentUser?.isBuyer && currentUser.companyMembership?.deactivatedAt) {
      this.router.replaceWith('deactivated');
    }
  }

  activate() {
    super.activate(...arguments);

    const { currentUser } = this.session;

    // GA and Sentry
    if (currentUser) {
      setUserId(this, currentUser.id);
      setSentryUser({ email: currentUser.email, id: currentUser.id });
    }

    const notAdmin = !currentUser?.isAdmin && !this.session.isMasquerading;
    const isProduction = config.APP_ENV === 'production';

    // Fullstory
    if (isProduction && notAdmin) {
      safeScriptLoad('https://edge.fullstory.com/s/fs.js', undefined, true);

      if (currentUser) {
        const userVars = filterNullable({
          displayName: currentUser.fullName,
          email: currentUser.email,
          mrating_real: currentUser.mrating,
          company_str: currentUser.company?.name,
          type_str: currentUser.type === 2 ? 'pub' : 'pro',
        });

        window.FS.identify(currentUser.id, userVars);
      }
    }

    // Zendesk
    if (currentUser && notAdmin) {
      safeScriptLoad('https://static.zdassets.com/ekr/snippet.js?key=cbb1e6b9-b8ed-46c2-850b-b42b64734fb2', 'ze-snippet');
    }

    // Intercom
    if (currentUser && notAdmin) {
      safeScriptLoad(`https://widget.intercom.io/widget/${config.INTERCOM_APP_ID}`);

      (async () => {
        try {
          const response = await this.api.get(`users/${currentUser.id}/bootstrap`);
          this.store.pushPayload(response);
          const data = this.store.peekRecord('user-bootstrap', response.data.id);

          const intercomSettings = {
            app_id: config.INTERCOM_APP_ID,
            user_id: currentUser.id,
            email: currentUser.email,
            name: currentUser.fullName,
            created_at: Math.floor(currentUser.createdAt.getTime() / 1000),
            user_hash: data.intercomHash,
          };

          if (currentUser.isBuyer) {
            intercomSettings.account_type = 'BUYER';

            if (currentUser.company) {
              intercomSettings.company = {
                id: currentUser.company.id,
                name: currentUser.company.name,
                tier: currentUser.company.tier,
                created_at: Math.floor(data.companyCreatedAt.getTime() / 1000),
              };
            }
          } else if (currentUser.isSeller) {
            intercomSettings.account_type = 'SELLER';
            intercomSettings.signup_step = currentUser.signupStep;
            intercomSettings.is_vetted = currentUser.isVetted;
            intercomSettings.is_vettable = currentUser.isVettable;
            intercomSettings.verified_projects = data.verifiedProjects;
            intercomSettings.awaiting_verification_projects = data.pendingVerification;
            intercomSettings.is_invited = data.isInvited;
            intercomSettings.is_referred = data.isReferred;
          }

          window.Intercom('boot', intercomSettings);
        } catch {
          // do nothing
        }
      })();
    }
  }

  /**
   * This function is called on EVERY transition.  A good place to put global interrupts.
   *
   * @inheritDoc
   */
  @action
  willTransition() {
    //close modals
    this.controller.closeModal();

    // This will look at the current route and ensure it's been added to our slug blocking list.  If this function
    // throws an error you should update the appropriate config files in:
    //
    // sh-app: ember-routes.json
    //
    // You can just copy and paste the JS versions over the current contents of the file.
    //
    // NOTE: This function is DEV MODE ONLY
    if (!isInDevMode()) {
      return;
    }

    const pathName = location.pathname;
    const routeParts = pathName.split('/');
    const firstLevelRoute = routeParts[1];

    if (!firstLevelRoute) {
      return;
    }

    if (!EmberRoutes.includes(firstLevelRoute)) {
      //because a slug can be any top level route, let's just check all users in the store.
      const allUsers = this.store.peekAll('user');

      const foundUser = allUsers.find((user) => {
        return user.slug === firstLevelRoute;
      });

      if (!foundUser) {
        throw new Error('YOU NEED TO UPDATE TWO FILES REFERENCED ON THIS PAGE https://storyhunterlocal.com/all-routes');
      }
    }
  }
}

export default ApplicationRoute;
